/**
 * KI-Analyse (D-084, Prompt 127 Aufgabe 2).
 *
 *  - KiAnalysePanel: Sektion „KI-Analyse" im AssetDetail, direkt VOR dem
 *    BWA-Drilldown — Überschrift „Warum weicht EBTDA ab?", Abweichungs-Badge,
 *    Absätze, Listen „Treiber"/„Auffälligkeiten", Fußzeile Erzeugung/Modell.
 *  - KiFindings: aufklappbare Sektion „KI-Findings je Gesellschaft" unter den
 *    Summary-Kacheln der Portfolio-Seite, je Gesellschaft ein Aufklapp-Element.
 *
 * Fehlt das Feld (alter Snapshot), rendert NICHTS — keine Platzhalter.
 *
 * Badge-Ampel (Prompt 128 §2, Richtungssemantik D-041: EBTDA über Soll = günstig):
 * ≥ +10 % grün, ≤ −10 % rot, dazwischen neutral; null → KEIN Badge. Dieselbe Regel
 * (kiAmpel) in der AssetDetail-Sektion und im ki_summary-Block. Farben aus den
 * vorhandenen Ampel-Tokens (Muster des fehlt_2026-Badges in IntercompanyTabelle).
 *
 * Text wird ausschließlich als React-Text gerendert (kein innerHTML) — der Inhalt
 * stammt aus einem Sprachmodell und wird nicht als Markup interpretiert.
 */
import { useId } from 'react';
import type { KiAnalyse, KiSummaryEintrag } from '../types/snapshot';
import { formatPct } from '../lib/format';
import { formatZeitpunkt } from './Footer';

export const KI_TEXTE = {
  panelLabel: 'KI-Analyse',
  panelTitel: 'Warum weicht EBTDA ab?',
  treiber: 'Treiber',
  auffaelligkeiten: 'Auffälligkeiten',
  findingsTitel: 'KI-Findings je Gesellschaft',
} as const;

/** Schwelle der Badge-Ampel (Prompt 128 §2). Grenzwerte gehören zur Farbe. */
export const KI_AMPEL_SCHWELLE = 0.1;

export type KiAmpel = 'green' | 'red' | 'neutral';

/**
 * Farbe des Abweichungs-Badges; null → kein Badge.
 * Verglichen wird der auf 0,1 %-Punkte GERUNDETE Wert — derselbe, den formatPct
 * anzeigt. Sonst stünde bei 0,09996 „10,0 %" neutral neben „10,0 %" grün. Die
 * Rundung beseitigt nebenbei Float-Reste (0,1 kommt nie als 0,0999999… an die Grenze).
 */
export function kiAmpel(pct: number | null | undefined): KiAmpel | null {
  // Nur echte Zahlen — ein Fremdformat (String, bool) bekommt kein Badge statt
  // „NaN %" (Review-Befund 3).
  if (typeof pct !== 'number' || Number.isNaN(pct)) return null;
  // Betrag runden, Vorzeichen wieder anfügen: Intl rundet Halbwerte VON NULL WEG,
  // Math.round Richtung +∞. Ohne das stünde −0,0995 als „-10,0 %" neutral statt rot
  // (Review-Befund 1).
  const angezeigt = (Math.sign(pct) * Math.round(Math.abs(pct) * 1000)) / 1000;
  if (angezeigt >= KI_AMPEL_SCHWELLE) return 'green';
  if (angezeigt <= -KI_AMPEL_SCHWELLE) return 'red';
  return 'neutral';
}

const BADGE_KLASSE: Record<KiAmpel, string> = {
  green: 'border border-ampel-green/40 bg-ampel-green/10 text-ampel-green',
  red: 'border border-ampel-red/40 bg-ampel-red/10 text-ampel-red',
  neutral: 'border border-transparent bg-surface-2 text-text-muted',
};

function KiAbweichungBadge({ pct }: { pct: number | null | undefined }) {
  const ampel = kiAmpel(pct);
  if (!ampel) return null; // null/kein Zahlwert → kein Badge (Prompt 128 §2), nie „0 %"
  return (
    <span
      data-ki-ampel={ampel}
      className={`whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium tabular-nums ${BADGE_KLASSE[ampel]}`}
      title="EBTDA-Abweichung laut KI-Analyse (grün ≥ +10 %, rot ≤ −10 %)"
    >
      EBTDA {formatPct(pct)}
    </span>
  );
}

/** `\n`-getrennte Absätze; Leerzeilen erzeugen keine leeren <p>. */
function absaetze(text: string | undefined): string[] {
  return (text ?? '')
    .split(/\n+/)
    .map((a) => a.trim())
    .filter(Boolean);
}

function KiListe({ titel, eintraege }: { titel: string; eintraege: string[] | undefined }) {
  const id = useId(); // eindeutig, auch wenn die Sektion mehrfach auf der Seite steht
  if (!eintraege?.length) return null;
  return (
    <div>
      <h4 id={id} className="mb-1 text-sm font-medium text-text">
        {titel}
      </h4>
      <ul aria-labelledby={id} className="list-disc space-y-0.5 pl-5 text-sm text-text">
        {eintraege.map((e, i) => (
          <li key={`${i}-${e}`}>{e}</li>
        ))}
      </ul>
    </div>
  );
}

export function KiAnalysePanel({ analyse }: { analyse?: KiAnalyse | null }) {
  if (!analyse) return null;
  return (
    <section
      aria-label={KI_TEXTE.panelLabel}
      className="rounded-lg border border-border bg-surface p-4"
    >
      <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
        {KI_TEXTE.panelLabel}
      </p>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-lg font-semibold text-text">{KI_TEXTE.panelTitel}</h3>
        <KiAbweichungBadge pct={analyse.abweichung_ebtda_pct} />
      </div>
      <div className="space-y-2 text-sm text-text">
        {absaetze(analyse.text).map((a, i) => (
          <p key={i}>{a}</p>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <KiListe titel={KI_TEXTE.treiber} eintraege={analyse.treiber} />
        <KiListe titel={KI_TEXTE.auffaelligkeiten} eintraege={analyse.auffaelligkeiten} />
      </div>
      <p className="mt-3 text-xs text-text-muted">
        Erzeugt am {formatZeitpunkt(analyse.erzeugt)} · Modell {analyse.modell}
      </p>
    </section>
  );
}

export function KiFindings({ eintraege }: { eintraege?: KiSummaryEintrag[] | null }) {
  if (!eintraege?.length) return null;
  return (
    <section aria-label={KI_TEXTE.findingsTitel}>
      <details className="rounded-lg border border-border bg-surface">
        <summary className="cursor-pointer px-4 py-3 text-lg font-semibold text-text">
          {KI_TEXTE.findingsTitel}
        </summary>
        <div className="divide-y divide-border border-t border-border">
          {eintraege.map((e) => (
            <details key={e.slug || e.short_name} className="px-4 py-2">
              <summary className="flex cursor-pointer flex-wrap items-center gap-2 text-sm font-medium text-text">
                <span>{e.short_name}</span>
                <KiAbweichungBadge pct={e.abweichung_ebtda_pct} />
              </summary>
              {e.kernpunkte?.length ? (
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm text-text">
                  {e.kernpunkte.map((k, i) => (
                    <li key={`${i}-${k}`}>{k}</li>
                  ))}
                </ul>
              ) : null}
            </details>
          ))}
        </div>
      </details>
    </section>
  );
}
